/**
 * Custom schema for framed header text
 * Displays text in a framed box (centered)
 */

import { PDF_TITLE_SIZE } from "./template";

/**
 * Draw a framed header text
 */
async function drawFramedHeader(arg: {
  pdfDoc: import("pdf-lib").PDFDocument;
  page: import("pdf-lib").PDFPage;
  schema: {
    position: { x: number; y: number };
    width: number;
    height: number;
  };
  value: string;
}): Promise<void> {
  const { rgb, StandardFonts } = await import("pdf-lib");
  const { value } = arg;

  if (!value || value.trim().length === 0) {
    return;
  }

  const { x, y } = arg.schema.position;
  const { width, height } = arg.schema;
  const pageHeight = arg.page.getHeight();

  // Convert mm to points
  const MM_TO_PT = 2.83465;
  const startX = x * MM_TO_PT;
  const startY = pageHeight - y * MM_TO_PT; // Top of schema area (PDF coordinates)

  const font = await arg.pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fontSize = PDF_TITLE_SIZE; // Use central font size definition
  const schemaHeightPt = height * MM_TO_PT;

  const text = value.trim();
  const textWidth = font.widthOfTextAtSize(text, fontSize);
  const frameWidth = textWidth + 8; // padding
  const frameHeight = fontSize + 6;
  const frameX = startX + (width * MM_TO_PT - frameWidth) / 2;

  // Center frame vertically in schema area
  const frameTopY = startY - (schemaHeightPt - frameHeight) / 2;
  const frameBottomY = frameTopY - frameHeight;

  // Draw frame
  arg.page.drawRectangle({
    x: frameX,
    y: frameBottomY,
    width: frameWidth,
    height: frameHeight,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
    color: undefined,
  });

  // Draw text (centered in frame)
  arg.page.drawText(text, {
    x: frameX + 4,
    y: frameBottomY + frameHeight / 2 - fontSize / 3,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  });
}

export const framedHeaderSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "#f5f5f5";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.fontSize = "10px";
    div.style.color = "#666";
    div.style.border = "1px solid #000";
    div.style.padding = "2px 4px";
    div.textContent = arg.value || "";
    arg.rootElement.appendChild(div);
  },

  pdf: drawFramedHeader,

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "framedHeader",
      type: "framedHeader",
      position: { x: 0, y: 0 },
      width: 95,
      height: 6,
    },
    defaultValue: "",
  },
};

export default framedHeaderSchema;
